
import {getActiveShopRegion, getActiveShopLang, whenReady} from '../general/Util';
import GoogleHandler from "../general/Google";
import {checkConsent} from '../general/UserCentrics';

let widgetCount = 0;

/**
 * @see https://developers.google.com/maps/documentation/javascript/examples/places-autocomplete-addressform
 */
class AutocompleteStreet {

	widgetId = 0;

	sessionToken = null;

	request = {
		includedPrimaryTypes: [ "street_address" ],
		language: getActiveShopLang(),
		region: getActiveShopRegion(),
		// origin: getUserLocation()
	}

	lastRequest = null;

	constructor( inputEl ) {
		this.widgetId = widgetCount++;

		this.inputEl = inputEl;
		this.fieldEl = inputEl.closest( '.o-field, .input_wrapper' );

		if ( this.fieldEl ) {

			// Set Aria-Roles.
			this.inputEl.role = 'combobox';
			this.inputEl.ariaAutoComplete = 'list';

			// KeyEvents.
			this.inputEl.addEventListener( 'input', () => this.onInput() );
			this.fieldEl.addEventListener( 'keydown', ( ev ) => this.onKeyDown( ev ) )

			// Show / Hide when focus changes.
			this.fieldEl.addEventListener( 'focusin', () => this.onFocus() );
			this.fieldEl.addEventListener( 'focusout', () => this.onBlur() );

			// Get or add resultsContainerEl.
			this.resultsContainerEl = this.fieldEl.querySelector( '.o-field__suggestions' ) || ( () => {
				const resultsContainerEl = document.createElement( 'ul' );
				resultsContainerEl.className = 'o-field__suggestions';
				resultsContainerEl.role = 'listbox'
				this.fieldEl.appendChild( resultsContainerEl );
				return resultsContainerEl;
			} )();

			// Mark resultsContainer as Part of the Input for ScreenReaders.
			this.resultsContainerEl.id = this.resultsContainerEl.id || 'acl-gal-' + this.widgetId;
			this.inputEl.setAttribute( 'aria-owns', this.resultsContainerEl.id );
		}
	}

	onFocus() {
		if ( this.fieldEl.matches( ':focus-within' ) ) {
			this.resultsContainerEl.classList.add( 'is-visible' );
			this.resultsContainerEl.ariaExpanded = true;
		}

		if ( ! this.lastRequest ) {
			this.onChange();
		}
	}

	onBlur() {
		if ( ! this.fieldEl.matches( ':focus-within' ) ) {
			this.resultsContainerEl.classList.remove( 'is-visible' );
			this.resultsContainerEl.ariaExpanded = false;
		}
	}

	onKeyDown( ev ) {
		let direction = 0;
		switch ( ev.keyCode ) {
			case 38:
				direction = -1;
				break;
			case 40:
				direction = 1;
				break;
			default:
				return;
		}

		ev.preventDefault();
		const buttonEls = Array.from( this.resultsContainerEl.querySelectorAll( '.o-field__suggestions button' ) );
		if ( ! this.lastRequest && 40 === ev.keyCode ) {
			this.onChange().then( () => this.onKeyDown( ev ) );
			return;
		}

		const focusEls = [ this.inputEl, ...buttonEls ];
		const currentIndex = focusEls.findIndex( ( focusEl ) => focusEl.matches( ':focus' ) ) || 0;
		const fallbackEl = direction > 0 ? this.inputEl : focusEls.findLast( () => true );
		const nextEl = focusEls[ currentIndex + direction ] ?? fallbackEl;
		if ( nextEl ) {
			nextEl.focus();
		}
	}

	onInput = window.EN_KO.lodash.debounce( () => this.onChange(), 0 );

	onChange() {
		// Get Input Value to Search.
		const input = this.inputEl.value;

		const myRequest = this.lastRequest = this.searchAddress( input );
		return this.lastRequest.then( ( suggestions ) => {
			// Only handle the last request, to avoid race conditions.
			if ( myRequest !== this.lastRequest ) {
				return;
			}

			// Clear the list first.
			this.resultsContainerEl.replaceChildren();
			for ( const suggestion of suggestions ) {
				const placePrediction = suggestion.placePrediction;
				// Create a link for the place, add an event handler to fetch the place.
				const buttonEl = document.createElement( 'button' );
				buttonEl.type = 'button';
				buttonEl.addEventListener( 'click', () => {
					this.onPlaceSelected( placePrediction.toPlace() );
				} );

				if ( placePrediction.secondaryText ) {
					buttonEl.innerHTML = `<strong>${placePrediction.mainText.toString()}</strong>, ${placePrediction.secondaryText.toString()}`;
				} else {
					buttonEl.innerHTML = `<strong>${placePrediction.mainText.toString()}</strong>`;
				}

				// Create a new list item element.
				const listEl = document.createElement( 'li' );
				listEl.role = 'option';
				listEl.appendChild( buttonEl );

				// Add ListItem to List.
				this.resultsContainerEl.appendChild( listEl );
			}
		} );
	}

	getSessionToken() {
		const { AutocompleteSessionToken  } = window.google.maps.places;
		return this.sessionToken ??= new AutocompleteSessionToken();
	}

	async searchAddress( input ) {

		if ( input.length <= 0 ) {
			return [];
		}

		const { AutocompleteSuggestion  } = window.google.maps.places;

		// Create a session token.
		const sessionToken = this.getSessionToken();

		// Build Request Data.
		const request = {
			input,
			sessionToken,
			...this.request
		};

		const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions( request );
		return suggestions;
	}

	async onPlaceSelected( place )   {

		await place.fetchFields( {
			fields: [ 'addressComponents' ],
		} );

		// Reset SessionToken and last request.
		this.inputEl.focus();
		this.sessionToken = null;
		this.lastRequest = null;

		// Remove UL children.
		this.resultsContainerEl.replaceChildren();

		// Fill Inputs.
		this.fillInAddress( place.addressComponents );
	}

	fillInAddress( addressComponents ) {

		const containerEl = this.inputEl.closest( 'form, fieldset' );

		let streetno = ""
		let address1 = "";
		let subpremise = "";
		let postcode = "";
		let cityname = "";
		let state  = "";
		let country = "AT";

		// Get each component of the address from the place details,
		// and then fill-in the corresponding field on the form.
		// place.address_components are google.maps.GeocoderAddressComponent objects
		// which are documented at http://goo.gle/3l5i5Mr
		for ( const component of addressComponents ) {
			const [ componentType ] = component.types;

			switch ( componentType ) {
				case "street_number":
					streetno = component.longText;
					break;

				case "route":
					address1 = component.longText;
					break;

				case "subpremise":
					subpremise = component.longText;
					break;

				case "postal_code":
					postcode = component.longText;
					break;

				case "postal_code_suffix":
					postcode = `${postcode}-${component.longText}`;
					break;

				case "locality":
					cityname = component.longText;
					break;

				case "administrative_area_level_1":
					state = component.shortText; // eslint-disable-line no-unused-vars
					break;

				case "country":
					country = component.shortText;
					break;
			}
		}

		if ( address1 || streetno ) {
			const streetInputEls = containerEl.querySelectorAll( 'input[autocomplete*="street-address"]' );
			streetInputEls.forEach( ( streetInputEl ) => {
				streetInputEl.value = address1 + " " + streetno + ( subpremise ? `/${subpremise}` : '' );
				streetInputEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			} )
		}

		if ( postcode ) {
			const postalInputEls = containerEl.querySelectorAll( 'input[autocomplete*="postal-code"]' );
			postalInputEls.forEach( ( postalInputEl ) => {
				postalInputEl.value = postcode;
				postalInputEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			} )
		}

		if ( cityname ) {
			const cityInputEls = containerEl.querySelectorAll( 'input[autocomplete*="address-level2"]' );
			cityInputEls.forEach( ( cityInputEl ) => {
				cityInputEl.value = cityname;
				cityInputEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			} );
		}

		if ( country ) {
			const countrySelectEls = containerEl.querySelectorAll( 'select[autocomplete*="country"]' );
			countrySelectEls.forEach( ( countrySelectEl ) => {
				const optionEls = Array.from( countrySelectEl.options );
				const foundEl = optionEls.find( ( optionEl ) => optionEl.dataset.iso2 === country );
				if ( foundEl ) {
					foundEl.selected = true;
				}
				countrySelectEl.dispatchEvent( new Event( 'change', { bubbles: true } ) );
			} );
		}

		this.inputEl.dispatchEvent( new CustomEvent( 'autocomplete_street.acl', { bubbles: true } ) );
	}

	static init = ( scopeEl = document ) => {
		const inputEls = scopeEl.querySelectorAll( '.acl-google-autocomplete' );
		if ( inputEls.length > 0 ) {
			checkConsent( 'Google Maps Places' ).then( ( hasConsent ) => {
				if ( hasConsent ) {
					// Make sure Google Maps and the places library are loaded.
					GoogleHandler.getGoogleApi( ['places'] ).then( () => {
						inputEls.forEach( inputEl => {
							inputEl.KOE_GOOGLE_AUTOCOMPLETE_STREET ??= new AutocompleteStreet( inputEl );
						} );
					} );
				}
			} );
		}
	}
}


whenReady.then( () => {

	// Setup Autocomplete initialisation.
	AutocompleteStreet.init();
} )

export default AutocompleteStreet;
